import React, { useState, useEffect } from "react";
import { Modal, Box, Typography, IconButton, Paper, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OrderPathMap from './OrderPathMap';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import {
    ADDRESS_TYPE_MERCHANT, ADDRESS_TYPE_ANOTHER_HUB, ADDRESS_TYPE_OWN_HUB,
    ADDRESS_TYPE_HUB_CUSTOMER, ADDRESS_TYPE_MERCHANT_CUSTOMER
} from 'src/utils/pnd/PndConstants';

const OrderPathMapModal = ({ show, toggle, data }) => {
    const [pickupCoords, setPickupCoords] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (show && data) {
            const fetchPickupCoords = async () => {
                setLoading(true);
                try {
                    let coords = null;
                    const addressTypeId = data?.orderFromAddressTypeInfo?.orderAddressTypeId;

                    // Support for comma-separated strings in pickupCoordinates / dropOffCoordinates
                    const parseCoordString = (str) => {
                        if (typeof str !== 'string' || !str.includes(',')) return null;
                        const [lat, lng] = str.split(',').map(s => parseFloat(s.trim()));
                        return isFinite(lat) && isFinite(lng) ? { latitude: lat, longitude: lng } : null;
                    };

                    if (addressTypeId === ADDRESS_TYPE_MERCHANT) {
                        const res = await PndRestApi.GetMerchantById(data.fromUniqueId);
                        if (res.status === 200 && res.data.status === 200) {
                            coords = { latitude: res.data.data.latitude, longitude: res.data.data.longitude };
                        }
                    } else if (addressTypeId === ADDRESS_TYPE_ANOTHER_HUB || addressTypeId === ADDRESS_TYPE_OWN_HUB) {
                        const res = await PndRestApi.GetHubById(data.fromUniqueId);
                        if (res.status === 200 && res.data.status === 200) {
                            coords = { latitude: res.data.data?.latitude, longitude: res.data.data?.longitude };
                        }
                    }

                    // Fallback search in the main data object
                    if (!coords) {
                        coords = parseCoordString(data.pickupCoordinates) || parseCoordString(data.pickupCoord);
                        if (!coords) {
                            const lat = data.fromLatitude || data.pickupLatitude || data.latitude || data.lat;
                            const lng = data.fromLongitude || data.pickupLongitude || data.longitude || data.lng;
                            if (lat && lng) coords = { latitude: lat, longitude: lng };
                        }
                    }

                    setPickupCoords(coords);
                } catch (e) {
                    console.error("Map Coord Error:", e);
                } finally {
                    setLoading(false);
                }
            };
            fetchPickupCoords();
        }
    }, [show, data]);

    if (!data) return null;

    const parseCoordString = (str) => {
        if (typeof str !== 'string' || !str.includes(',')) return null;
        const [lat, lng] = str.split(',').map(s => parseFloat(s.trim()));
        return isFinite(lat) && isFinite(lng) ? { latitude: lat, longitude: lng } : null;
    };

    const pickup = {
        latitude: pickupCoords?.latitude,
        longitude: pickupCoords?.longitude,
        address: data.pickupAddress || data.fromName
    };

    const dropoffCoords = parseCoordString(data.dropOffCoordinates) || parseCoordString(data.dropOffCoord);

    const dropoff = {
        latitude: dropoffCoords?.latitude || data.toLatitude || data.deliveryLatitude || data.latitude || data.lat,
        longitude: dropoffCoords?.longitude || data.toLongitude || data.deliveryLongitude || data.longitude || data.lng,
        address: data.dropOffAddress || data.toName
    };

    return (
        <Modal open={show} onClose={toggle} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Paper elevation={24} sx={{ width: '90%', maxWidth: 1000, borderRadius: 4, overflow: 'hidden', bgcolor: 'background.paper', outline: 'none' }}>
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b' }}>Order Path - {data.orderNumber}</Typography>
                    <IconButton size="small" onClick={toggle} sx={{ color: '#64748b' }}><CloseIcon /></IconButton>
                </Box>

                <Box sx={{ p: 0, height: { xs: 500, md: 700 }, width: '100%', position: 'relative' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <OrderPathMap pickup={pickup} dropoff={dropoff} orderNo={data.orderNumber} />
                    )}
                </Box>
            </Paper>
        </Modal>
    );
};

export default OrderPathMapModal;
